import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams, useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ActuView from "./ActuView.js";
import { getMatchDetail, getPaginedMatchDetail } from "../../../../api/index.js";
import { GET_MATCH_DETAILS } from "../../../../constants/actionTypes.js";

const buildSearchString = (searchParams) => {
  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const normalizeActuKey = (value = '') => {
  return decodeURIComponent(String(value))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const ActuResolver = () => {
  const { actu } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { live, getIdActuFromActuName } = useOutletContext();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tags = queryParams.get("tags")?.split(",").filter(Boolean) || [];
  const selectedDayFromUrl = queryParams.get("day") || undefined;
  const tagsKey = tags.join(',');

  const actualitesDetails = useSelector((state) => state.live?.actualitesDetails || []);

  const fallbackActuFromStore = useMemo(() => {
    const normalizedRouteActu = normalizeActuKey(actu);

    return actualitesDetails.find((item) => {
      const itemId = item?.idActu || item?.idMatch;
      const itemName = item?.name || '';
      return (
        Boolean(itemId) &&
        normalizeActuKey(itemName) === normalizedRouteActu
      );
    });
  }, [actualitesDetails, actu]);

  const resolvedIdActu = useMemo(() => {
    const idFromHelper = getIdActuFromActuName ? getIdActuFromActuName(actu) : undefined;
    return idFromHelper || fallbackActuFromStore?.idActu || fallbackActuFromStore?.idMatch || undefined;
  }, [getIdActuFromActuName, actu, fallbackActuFromStore, live]);

  const actuDetail = useSelector((state) => {
    const list = state.live?.actualitesDetails || [];
    return list.find((item) => (item?.idActu || item?.idMatch) === resolvedIdActu);
  });

  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);

  useEffect(() => {
    console.log("ACTU RESOLVER STATE", {
      actuSlug: actu,
      resolvedIdActu,
      fallbackActuFromStore,
      hasFetchedInitial,
      hasActuDetail: Boolean(actuDetail),
      actualitesCount: actualitesDetails.length,
    });
  }, [actu, resolvedIdActu, fallbackActuFromStore, hasFetchedInitial, actuDetail, actualitesDetails]);

  useEffect(() => {
    if (!resolvedIdActu) return;

    let isMounted = true;

    const fetchActuDetail = async () => {
      setIsLoading(true);
      setHasFetchedInitial(false);

      try {
        const response = await getMatchDetail(resolvedIdActu, {
          tags,
          day: selectedDayFromUrl,
        });

        if (!isMounted || !response?.data) {
          return;
        }

        console.log("INITIAL ACTU DETAIL", {
          availableDays: response?.data?.availableDays,
          selectedDay: response?.data?.selectedDay,
          selectedDayCount: response?.data?.selectedDayCount,
          hasMore: response?.data?.hasMore,
          idActu: response?.data?.idActu,
          idMatch: response?.data?.idMatch,
        });

        dispatch({ type: GET_MATCH_DETAILS, payload: response.data });

        if (isMounted) {
          setHasFetchedInitial(true);
        }
      } catch (error) {
        console.error("Erreur lors du chargement du direct:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchActuDetail();

    return () => {
      isMounted = false;
    };
  }, [dispatch, resolvedIdActu, selectedDayFromUrl, tagsKey]);

  const resolvedSelectedDay = actuDetail?.selectedDay || actuDetail?.dayFeed?.selectedDay;
  const availableDays = actuDetail?.availableDays || actuDetail?.dayFeed?.availableDays || [];

  useEffect(() => {
    if (!resolvedSelectedDay) {
      return;
    }

    const requestedDayExists = selectedDayFromUrl
      ? availableDays.some((day) => day.date === selectedDayFromUrl)
      : false;

    // Cas normal : l'utilisateur a choisi un jour valide, on le laisse tranquille
    if (selectedDayFromUrl && requestedDayExists) {
      return;
    }

    // Cas initial : pas de day dans l'URL, ou jour demandé invalide
    if (selectedDayFromUrl === resolvedSelectedDay) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('day', resolvedSelectedDay);

    navigate(
      {
        pathname: location.pathname,
        search: buildSearchString(searchParams),
      },
      { replace: true }
    );
  }, [
    location.pathname,
    location.search,
    navigate,
    resolvedSelectedDay,
    selectedDayFromUrl,
    availableDays,
  ]);

  const handleSelectDay = (day) => {
    if (!day || day === resolvedSelectedDay) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('day', day);

    navigate({
      pathname: location.pathname,
      search: buildSearchString(searchParams),
    });
  };

const handleGoToPreviousDay = () => {
  const currentIndex = availableDays.findIndex((day) => day.date === resolvedSelectedDay);
  if (currentIndex === -1 || currentIndex >= availableDays.length - 1) return;

  // "Précédent" = jour plus ancien
  handleSelectDay(availableDays[currentIndex + 1].date);
};

const handleGoToNextDay = () => {
  const currentIndex = availableDays.findIndex((day) => day.date === resolvedSelectedDay);
  if (currentIndex <= 0) return;

  // "Suivant" = jour plus récent
  handleSelectDay(availableDays[currentIndex - 1].date);
};

  const loadMore = async () => {
    const currentId = actuDetail?.idActu || actuDetail?.idMatch || resolvedIdActu;

    if (!currentId || !actuDetail) {
      return;
    }

    try {
      const response = await getPaginedMatchDetail(currentId, actuDetail.pagination, {
        tags,
        day: resolvedSelectedDay,
      });

      if (!response?.data) {
        throw new Error("Réponse invalide de getPaginedMatchDetail");
      }

      console.log("INITIAL ACTU DETAIL 2", {
        availableDays: response?.data?.availableDays,
        selectedDay: response?.data?.selectedDay,
        selectedDayCount: response?.data?.selectedDayCount,
        hasMore: response?.data?.hasMore,
      });

      dispatch({ type: GET_MATCH_DETAILS, payload: response.data });
    } catch (error) {
      console.error("Erreur dans loadMore:", error);
      throw new Error("Échec du chargement des détails du match");
    }
  };

  if (!resolvedIdActu || isLoading || !hasFetchedInitial || !actuDetail) {
    return <CircularProgress />;
  }

  return (
    <ActuView
      actu={actuDetail}
      loadMore={loadMore}
      onSelectDay={handleSelectDay}
      onGoToPreviousDay={handleGoToPreviousDay}
      onGoToNextDay={handleGoToNextDay}
    />
  );
};

export default ActuResolver;